import { getShoppingCart } from "./utils"
import { IShoppingCartLine } from "./components/ShoppingCart/Line"

export const tagmanagerAddToShoppingCart = async (product) => {
    const { shoppingCartLines } = await getShoppingCart()
    dataLayerAddToShoppingCart(shoppingCartLines, product)
}

export const tagmanagerUpdateShoppingCart = async (action: string, id: string, quantity?: number) => {
    const { shoppingCartLines } = await getShoppingCart()
    const product = await shoppingCartLines.filter(line => line.id === id)[0] as IShoppingCartLine

    switch (action) {
        case "update": {
            dataLayerRemoveFromShoppingCart(shoppingCartLines, product)
            dataLayerAddToShoppingCart(shoppingCartLines, product, quantity)
            break
        }
        case "remove": {
            dataLayerRemoveFromShoppingCart(shoppingCartLines, product)
            break
        }
    }
}

export const tagmanagerObserveImpressions = (items: HTMLElement[], data: Object) => {
    const options = {
        root: null,
        rootMargin: "0px",
        threshold: .5
    }

    const onImpression = (entries, observer) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const id = entry.target.id
                dataLayerPushImpression(id, data)
                observer.unobserve(entry.target)
            }
        })
    }

    const impressionObserver = new IntersectionObserver(onImpression, options)

    items.forEach(item => impressionObserver.observe(item))
}

export const tagmanagerProductClickHandler = (products) => {
    const productClickHandler = (e) => {
        const product = e.target.closest("[data-tagmanager-product]") as HTMLElement
        dataLayerPushProductClick(product)
    }

    products.forEach(product => product.addEventListener("click", productClickHandler))
}

export const tagmanagerPromotionsClickHandler = (promotions, promotionsData) => {
    const onPromoClick = (e) => {
        const target = e.target.closest("[data-tagmanager-promo]") as HTMLElement
        const promo = (Object.values(promotionsData) as any[]).filter(promo => promo.PromotionsId === target.dataset.tagmanagerPromoId)[0]
        dataLayerPushPromotionClick(promo)
    }

    promotions.forEach(promo => promo.addEventListener("click", onPromoClick))
}

export const tagmanagerInitializePromotions = (promotions) => dataLayerPushPromotions(promotions)

export const tagmanagerPushCheckoutStep = async (step) => {
    const { shoppingCartLines } = await getShoppingCart()
    dataLayerPushCheckoutStep(step, shoppingCartLines)
}

export const tagmanagerPushProductDetailPage = (product) => dataLayerPushProductDetailPage(product)

export const getPromoData = (promo, slot, websiteType) => {
    if (Array.isArray(promo))
        return promo.map(x => getPromoData(x, slot, websiteType))

    return ({
        id: promo.PromotionsId,
        name: "www.cadeaukaarten.hema.nl", //promo.Button.Link.Url.toLowerCase(),
        creative: `${websiteType === 1 ? "home-business-contentslot" : "home-consumer-contentslot"}-${slot}|hero-banners|${slot === 0 ? "BigHeroBanner" : "SmallHeroBanner"}`, // promo.Image.ImageUrl.toLowerCase(),
        position: `${slot}|www.cadeaukaarten.hema.nl/${getPromoLinkUrl(promo).toLowerCase()}`
    })
}

const getPromoLinkUrl = (promo) => {
    if (promo?.Button)
        return promo.Button.Link.Url

    return promo.Link.Url
}

export const dataLayerPushProductDetailPage = (product) => {
    window["dataLayer"].push({
        pageType: "pdp",
        ecommerce: {
            currencyCode: "EUR",
            detail: {
                products: [{
                    name: product.productName,
                    id: product.productId,
                    price: product.fixedPrice ? product.fixedPrice : product.minCardPrice,
                    category: product.productType
                }]
            }
        }
    })
}

export const dataLayerPushCheckoutStep = (step, shoppingCartLines) => {
    window["dataLayer"].push({
        pageType: "Overview",
        ecommerce: {
            currencyCode: "EUR",
            checkout: {
                actionField: {
                    step: step
                },
                products: shoppingCartLines.map(line => ({
                    name: line.productName,
                    id: line.productId,
                    quantity: line.quantity,
                    price: line.price,
                    category: line.productType
                }))
            }
        }
    })
}

export const dataLayerPushProductClick = (product) => {
    const { tagmanagerName, tagmanagerSku, tagmanagerPrice, tagmanagerCategory } = product.dataset
    window["dataLayer"].push({
        event: "productClick",
        ecommerce: {
            currencyCode: "EUR",
            click: {
                products: [{
                    name: (tagmanagerName as string).toLowerCase(),
                    id: tagmanagerSku,
                    price: tagmanagerPrice,
                    category: tagmanagerCategory
                }]
            }
        }
    })
}

export const dataLayerPushPromotions = (promotions) => {
    window["dataLayer"].push({
        ecommerce: {
            promoView: {
                promotions: Object.values(promotions).map((promo, index) => getPromoData(promo, index, window["shoppingCart"].websiteType))
            }
        }
    })
}

export const dataLayerPushPromotionClick = (promo) => {
    const data = getPromoData(promo, 1, window["shoppingCart"].websiteType)
    window["dataLayer"].push({
        event: "promotionClick",
        ecommerce: {
            promoView: {
                promotions: [data]
            }
        }
    })
}

export const dataLayerPushImpression = (type, products) => {
    type = type === "egifts" ? "eGifts" : "giftCards"
    window["dataLayer"].push({
        event: "sendImpression",
        ecommerce: {
            currencyCode: "EUR",
            impressions: products[type].map(gift => ({
                name: gift.Name.toLowerCase(),
                id: gift.ProductCode.toLowerCase(),
                price: gift.OverviewPrice,
                category: gift.ProductType
            }))
        }
    })
}


export const dataLayerAddToShoppingCart = (shoppingCartLines, product, newQuantity?) => {
    const { productName, productId, productType, price, quantity: quantity } = product

    window["dataLayer"].push({
        event: "addToCart",
        itemsInCart: shoppingCartLines.length,
        productsInCart: shoppingCartLines.map(line => ({
            name: line.productName,
            id: line.productId,
            quantity: line.quantity,
            price: line.price,
            category: line.productType
        })),
        eventLocation: "pdp",
        ecommerce: {
            currencyCode: "EUR",
            add: {
                actionField: {
                    list: 'homepage product overview'
                },
                products: [{
                    name: productName,
                    id: productId,
                    quantity: newQuantity || quantity,
                    price: price,
                    category: productType
                }]
            }
        }
    })
}

export const dataLayerRemoveFromShoppingCart = (shoppingCartLines, product) => {
    const { productName, productId, productType, price, quantity } = product

    window["dataLayer"].push({
        event: "removeFromCart",
        itemsInCart: shoppingCartLines.length,
        productsInCart: shoppingCartLines.map(line => ({
            name: line.productName.toLowerCase(),
            id: line.productId,
            quantity: line.quantity,
            price: line.price,
            category: line.productType
        })),
        eventLocation: "pdp",
        ecommerce: {
            currencyCode: "EUR",
            remove: {
                products: [{
                    name: productName.toLowerCase(),
                    id: productId,
                    quantity: quantity,
                    price: price,
                    category: productType
                }]
            }
        }
    })
}
